import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  resource,
  signal,
} from '@angular/core';
import { User } from '@core/interfaces/user.interface';
import { SocketService } from '@core/services/socket.service';
import { SessionService } from '@core/services/session.service';
import { UserHistoryService } from '../services/user-history.service';
import { PaginationState } from '@tanstack/angular-table';
import {
  transactionsHistoryStore,
  transactionsHistorySettings,
} from '../configs/transactions-history-store.config';
import {
  NgpTabset,
  NgpTabList,
  NgpTabButton,
  NgpTabPanel,
} from 'ng-primitives/tabs';
import { SvgIconComponent } from 'angular-svg-icon';
import { TableComponent } from '@core/components/table/table.component';
import { KeyValuePipe } from '@angular/common';
import { NgScrollbarModule } from 'ngx-scrollbar';

@Component({
  selector: 'app-user-transactions-history',
  imports: [
    NgpTabset,
    NgpTabList,
    NgpTabButton,
    NgpTabPanel,
    KeyValuePipe,
    NgScrollbarModule,
    TableComponent,
    SvgIconComponent,
  ],
  templateUrl: './user-transactions-history.component.html',
  styleUrl: './user-transactions-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class UserTransactionsHistoryComponent {
  private socketService = inject(SocketService);
  public session = inject(SessionService);
  public userHistoryService = inject(UserHistoryService);

  user = input.required<User>();
  active = input<boolean>(false);

  tabs = signal(transactionsHistorySettings);
  readonly selectedTab =
    signal<keyof typeof transactionsHistorySettings>('Deposits');

  storeResource = resource({
    params: () => {
      if (!this.active()) return undefined;
      return {
        endpoint: this.tabs()[this.selectedTab()].endpoint,
        steamid: this.user().steamid,
      };
    },
    loader: async ({ params }) => {
      if (!params) return undefined;
      const data = await this.socketService.emit(params.endpoint, {
        steamid: params.steamid,
      });
      return data;
    },
  });

  store = computed(() => {
    return {
      ...transactionsHistoryStore,
      [this.selectedTab()]: this.storeResource.value(),
    };
  });

  readonly pagination = model<PaginationState>({
    pageIndex: 0,
    pageSize: 14,
  });

  selectedTabResetEffect = effect(() => {
    if (this.selectedTab()) {
      this.pagination.update((prev) => ({
        pageSize: prev.pageSize,
        pageIndex: 0,
      }));
    }
  });
}
